// WOW Journey — bilingual itinerary planner (wizard + results in one page).
// Gated by REACT_APP_WOW_JOURNEY_ENABLED. Zero paid APIs. Rules-based engine.
import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Printer, MessageSquare, RefreshCw, Compass } from "lucide-react";
import SEO from "../../components/SEO";
import { useLocale } from "../../i18n";
import { WJ, wj } from "./i18n";
import { createPlan, refinePlan, FEATURE_ENABLED } from "../../lib/wowJourneyApi";

// ---------------------------------------------------------- map URL builders (coordinate-based)
// IMPORTANT: Build URLs from VALIDATED coordinates — never from localized city names.
// "Brasov" without diacritic matched Str. Brașov in Sector 5, Bucharest when visitors
// switched to RO. Coordinates are language-independent and stable.
//
// Why we use an honest location overview + a separate directions link:
// Google's free iframe embed (maps.google.com/maps?...&output=embed) does NOT reliably
// render multi-stop directions on first load — it sometimes shows only endpoint markers
// and only draws the full polyline on cached re-renders. Since we can't use the paid
// Directions Embed API, we show a location-overview embed centered on the trip start
// and provide a prominent, complete directions URL (which DOES support multi-stop with
// return) that opens in Google Maps.
function buildMapUrls(candidate, locale) {
  const days = candidate?.days || [];
  if (days.length === 0) return { embedUrl: null, directionsUrl: null, stops: [], startName: "" };
  // Chronological stops: start base → each day's scheduled visits → new overnight base → return.
  // Cities go to Google by name (so they resolve to the city, not a nearby business);
  // attractions go by their validated catalog coordinates.
  const baseStop = (id, en, ro) => ({ id, kind: "base", name: locale === "ro" ? ro : en, query: `${en}, Romania` });
  const first = days[0];
  const seq = [baseStop(first.base_id, first.base_name_en, first.base_name_ro)];
  let curBase = first.base_id;
  days.forEach((d, idx) => {
    if (d.base_id !== curBase && idx > 0 && d.activities.length === 0) {
      seq.push(baseStop(d.base_id, d.base_name_en, d.base_name_ro));
    }
    for (const a of d.activities) {
      if (a.lat == null || a.lon == null) continue;
      seq.push({ id: a.poi_id, kind: "poi", name: locale === "ro" ? a.name_ro : a.name_en, query: `${a.lat},${a.lon}` });
    }
    if (d.base_id !== curBase && d.activities.length > 0) {
      seq.push(baseStop(d.base_id, d.base_name_en, d.base_name_ro));
    }
    curBase = d.base_id;
  });
  if (candidate.return_to_start && curBase !== first.base_id) {
    seq.push(baseStop(first.base_id, first.base_name_en, first.base_name_ro));
  }
  const stops = seq.filter((s, i) => i === 0 || s.id !== seq[i - 1].id);
  // Google Maps URLs accept at most 10 points; drop the latest visits (never bases) if over.
  while (stops.length > 10) {
    const k = stops.map((s) => s.kind).lastIndexOf("poi");
    if (k < 0) break;
    stops.splice(k, 1);
  }
  const directionsUrl = `https://www.google.com/maps/dir/${stops.map((s) => encodeURIComponent(s.query)).join("/")}`;
  const embedUrl = `https://maps.google.com/maps?q=${encodeURIComponent(`${first.base_lat},${first.base_lon}`)}&z=7&output=embed`;
  return { embedUrl, directionsUrl, stops, startName: stops[0]?.name || "" };
}

// ---------------------------------------------------------- small UI helpers
const Chip = ({ active, children, onClick, dataTestid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={dataTestid}
    className={
      "inline-flex items-center px-4 py-2 rounded-full border transition-colors duration-200 text-[14px] " +
      (active
        ? "bg-[var(--wr-ink)] text-[var(--wr-bg)] border-[var(--wr-ink)]"
        : "bg-transparent text-[var(--wr-ink)] border-[var(--wr-border)] hover:border-[var(--wr-ink)]")
    }
  >
    {children}
  </button>
);

const Section = ({ children }) => (
  <div className="mb-9">{children}</div>
);

const Label = ({ children }) => (
  <label className="block font-mono-wr text-[11px] uppercase tracking-[0.25em] text-[var(--wr-ink-2)] mb-3">{children}</label>
);

// --------------------------------------------------------------- defaults
const DEFAULT_REQ = {
  start_city: "",
  start_undecided: false,
  season: null,
  sightseeing_days: 5,
  visiting_family: false,
  family_base: "",
  family_blocked_days: 0,
  end_city: "",
  end_undecided: false,
  transport: "undecided",
  interests: [],
  pace: "balanced",
  group: "couple",
  group_size: 2,
  children: false,
  budget: "not_sure",
  no_hiking: false,
  minimize_stairs: false,
  avoid_long_drives: false,
  avoid_mountain_roads: false,
  avoid_after_dark: false,
  keep_one_base: false,
  must_see: [],
  must_avoid: [],
  locale: "en",
};

// ----------------------------------------------------------- Day card
function DayCard({ day, locale }) {
  const periodLabel = (p) =>
    p === "morning" ? wj("r.morning", locale) : p === "afternoon" ? wj("r.afternoon", locale) : wj("r.evening", locale);
  const baseName = locale === "ro" ? day.base_name_ro : day.base_name_en;
  return (
    <div className="border border-[var(--wr-border)] rounded-sm p-6 bg-[var(--wr-surface-2)]" data-testid={`wj-day-${day.day_number}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
        <h3 className="font-editorial text-2xl text-[var(--wr-ink)]">
          {wj("r.day_label", locale, { n: day.day_number })} —{" "}
          <span className="text-[var(--wr-ink-2)]">
            {wj("r.based_in", locale)} {baseName}
          </span>
        </h3>
        {day.transfer_hours_estimate > 0 && (
          <span className="font-mono-wr text-[11px] uppercase tracking-widest text-[var(--wr-red)]">
            ~{day.transfer_hours_estimate.toFixed(1)}h · {wj("r.transfer", locale)}
          </span>
        )}
      </div>
      {(day.is_relocation_day || day.note_en || day.note_ro) && (locale === "ro" ? day.note_ro : day.note_en) && (
        <p className="text-[13px] italic text-[var(--wr-ink-2)] mb-4" data-testid={`wj-day-${day.day_number}-note`}>
          {locale === "ro" ? day.note_ro : day.note_en}
        </p>
      )}
      {day.activities.length === 0 ? (
        <p className="text-[14px] text-[var(--wr-ink-2)] italic">
          {locale === "ro" ? "Zi flexibilă — pauză sau explorare liberă." : "Flexible day — rest or free exploration."}
        </p>
      ) : (
        <div className="space-y-4">
          {day.activities.map((a) => (
            <div key={a.poi_id} className="flex gap-5">
              <div className="w-28 shrink-0">
                <p className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)]">{periodLabel(a.period)}</p>
                <p className="font-mono-wr text-[10px] text-[var(--wr-ink-2)]/70 mt-1">~{a.visit_hours}h</p>
              </div>
              <div className="flex-1">
                <p className="text-[16px] text-[var(--wr-ink)] font-medium">
                  {locale === "ro" ? a.name_ro : a.name_en}
                </p>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-[var(--wr-ink-2)]">
                  {a.stairs_level === "high" && <span>⚠ {wj("r.stairs_high", locale)}</span>}
                  {a.stairs_level === "moderate" && <span>· {wj("r.stairs_moderate", locale)}</span>}
                  {a.mountain_road && <span>· {wj("r.mountain_road", locale)}</span>}
                </div>
                {a.sources && a.sources.length > 0 && (
                  <p className="mt-1 text-[12px]">
                    <span className="text-[var(--wr-ink-2)]">{wj("r.sources", locale)}: </span>
                    {a.sources.map((s, i) => (
                      <a key={i} href={s} target="_blank" rel="noopener noreferrer" className="text-[var(--wr-red)] underline mr-2">
                        {new URL(s).hostname.replace("www.", "")}
                      </a>
                    ))}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="mt-5 text-[12px] italic text-[var(--wr-ink-2)]/80" data-testid={`wj-day-${day.day_number}-overnight`}>
        {day.overnight_excluded
          ? wj("r.return_no_acc", locale, { city: locale === "ro" ? day.overnight_city_ro : day.overnight_city_en })
          : `${wj("r.overnight", locale)}: ${locale === "ro" ? (day.overnight_city_ro || day.base_name_ro) : (day.overnight_city_en || day.base_name_en)}`}
      </p>
    </div>
  );
}

// -------------------------------------------------------- Candidate section
function CandidateView({ candidate, locale, onConcierge, isPrimary }) {
  if (!candidate) return null;
  const title = locale === "ro" ? candidate.title_ro : candidate.title_en;
  const why = locale === "ro" ? candidate.why_ro : candidate.why_en;
  const bass = candidate.overnight_bases
    .map((b) => {
      const name = locale === "ro" ? b.name_ro : b.name_en;
      if (b.is_return) return locale === "ro" ? `${name} (întoarcere)` : `${name} (return)`;
      return `${name} ×${b.nights}`;
    })
    .join(" → ");
  const dvLabel = {
    light: wj("r.driving_light", locale),
    moderate: wj("r.driving_moderate", locale),
    heavy: wj("r.driving_heavy", locale),
  }[candidate.driving_load] || candidate.driving_load;
  const budgetAssump = locale === "ro" ? candidate.budget_assumptions_ro : candidate.budget_assumptions_en;
  const { embedUrl, directionsUrl, stops, startName } = buildMapUrls(candidate, locale);

  return (
    <div className="mb-14" data-testid={`wj-candidate-${candidate.id}`}>
      {!isPrimary && (
        <p className="font-mono-wr text-[11px] uppercase tracking-[0.3em] text-[var(--wr-red)] mb-3">
          / {wj("r.alternatives", locale)}
        </p>
      )}
      <h2 className="font-editorial text-3xl sm:text-4xl text-[var(--wr-ink)] mb-3">{title}</h2>
      <p className="text-[16px] leading-relaxed text-[var(--wr-ink-2)] max-w-3xl">{why}</p>

      <div className="mt-6 grid sm:grid-cols-3 gap-4 border-y border-[var(--wr-border)] py-5">
        <div>
          <p className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)] mb-1">{wj("r.overnight_bases", locale)}</p>
          <p className="text-[14px] text-[var(--wr-ink)]">{bass}</p>
        </div>
        <div>
          <p className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)] mb-1">{wj("r.driving_load", locale)}</p>
          <p className="text-[14px] text-[var(--wr-ink)]">{dvLabel}</p>
        </div>
        <div>
          <p className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)] mb-1">{wj("r.budget_label", locale)}</p>
          <p className="text-[14px] text-[var(--wr-ink)]">{candidate.budget_min_ron.toLocaleString()}–{candidate.budget_max_ron.toLocaleString()} RON</p>
          <p className="text-[11px] text-[var(--wr-ink-2)] mt-1">{budgetAssump}</p>
        </div>
      </div>

      {isPrimary && embedUrl && (
        <div className="mt-8" data-testid="wj-map">
          <p className="font-mono-wr text-[11px] uppercase tracking-[0.3em] text-[var(--wr-ink-2)] mb-2">
            / {locale === "ro" ? "Hartă orientativă" : "Location overview"}
          </p>
          <div className="border border-[var(--wr-border)] bg-[var(--wr-surface-2)] overflow-hidden rounded-sm">
            {/* key tied to URL forces full iframe remount when candidate changes.
                No `loading="lazy"` so first paint always loads the embed. */}
            <iframe
              key={embedUrl}
              title="trip-location-overview"
              width="100%"
              height="320"
              referrerPolicy="no-referrer-when-downgrade"
              src={embedUrl}
            />
          </div>
          {stops.length > 0 && (
            <p className="mt-3 text-[13px] text-[var(--wr-ink)]" data-testid="wj-map-stops">
              <span className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)] mr-2">
                {locale === "ro" ? "Opriri (în ordine)" : "Stops (in order)"}:
              </span>
              {stops.map((s) => s.name).join("  →  ")}
            </p>
          )}
          <a href={directionsUrl} target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-2 mt-3 px-4 py-2 border border-[var(--wr-ink)] text-[var(--wr-ink)] hover:bg-[var(--wr-ink)] hover:text-[var(--wr-bg)] rounded-sm transition-colors"
             data-testid="wj-open-maps">
            <span className="font-mono-wr text-[11px] uppercase tracking-[0.2em]">
              {locale === "ro" ? "Deschide ruta completă în Google Maps" : "Open full route in Google Maps"}
            </span>
            <span>→</span>
          </a>
          <p className="mt-2 text-[11px] italic text-[var(--wr-ink-2)]/80">
            {locale === "ro"
              ? "Preview-ul arată zona de start. Harta completă cu toate opririle și întoarcerea se deschide în Google Maps — atracțiile folosesc coordonate validate, orașele sunt căutate după nume."
              : "Preview shows the start area. The full circuit with all stops and return opens in Google Maps — attractions use validated coordinates, cities are matched by name."}
          </p>
        </div>
      )}

      <div className="mt-8 space-y-4">
        {candidate.days.map((d) => <DayCard key={d.day_number} day={d} locale={locale} />)}
      </div>

      {/* Listings nearby — show only when at least one base has qualifying listings.
          'Straight-line proximity' is stated explicitly; no road routing is implied. */}
      {isPrimary && candidate.overnight_bases.some((b) => (b.nearby_listings || []).length > 0) && (
        <div className="mt-12 border-t border-[var(--wr-border)] pt-10" data-testid="wj-nearby">
          <p className="font-mono-wr text-[11px] uppercase tracking-[0.3em] text-[var(--wr-red)] mb-3">
            / {wj("r.nearby_header", locale)}
          </p>
          <p className="text-[13px] italic text-[var(--wr-ink-2)] mb-6">
            {wj("r.nearby_proximity", locale)}
          </p>
          <div className="space-y-8">
            {candidate.overnight_bases.map((b) => {
              const items = b.nearby_listings || [];
              if (items.length === 0) return null;
              const baseName = locale === "ro" ? b.name_ro : b.name_en;
              return (
                <div key={b.id} data-testid={`wj-nearby-base-${b.id}`}>
                  <p className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)] mb-3">
                    {wj("r.nearby_near_label", locale)} {baseName}
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {items.map((l) => {
                      const name = locale === "ro" ? (l.name_ro || l.name_en) : (l.name_en || l.name_ro);
                      return (
                        <a key={l.slug}
                           href={`/listings/${l.slug}`}
                           data-testid={`wj-nearby-item-${l.slug}`}
                           className="group flex gap-4 border border-[var(--wr-border)] hover:border-[var(--wr-ink)] bg-[var(--wr-surface-2)] p-3 rounded-sm transition-colors">
                          {l.hero_image_url ? (
                            <img src={l.hero_image_url} alt="" loading="lazy"
                                 className="w-24 h-20 object-cover shrink-0 rounded-sm" />
                          ) : (
                            <div className="w-24 h-20 shrink-0 bg-[var(--wr-border)] rounded-sm" aria-hidden="true" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-[15px] text-[var(--wr-ink)] font-medium leading-snug line-clamp-2">{name}</p>
                            <p className="text-[11px] text-[var(--wr-ink-2)] mt-1 flex items-center gap-2">
                              <span>{l.listing_type}</span>
                              <span className="text-[var(--wr-border)]">•</span>
                              <span>~{l.distance_km} km</span>
                            </p>
                            <p className="mt-2 text-[11px] text-[var(--wr-red)] font-mono-wr uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">
                              {wj("r.nearby_open", locale)}
                            </p>
                          </div>
                        </a>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="mt-6 text-[12px] italic text-[var(--wr-ink-2)]/80">
            {wj("r.nearby_disclaimer", locale)}
          </p>
        </div>
      )}

      {isPrimary && (
        <div className="mt-10 flex flex-wrap gap-3 print-hide">
          <button onClick={onConcierge} data-testid="wj-concierge-cta"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-[var(--wr-ink)] text-[var(--wr-bg)] hover:bg-[var(--wr-red)] rounded-sm">
            <MessageSquare size={16} />
            <span className="font-mono-wr text-[12px] uppercase tracking-[0.2em]">{wj("r.discuss", locale)}</span>
          </button>
          <button onClick={() => window.print()} data-testid="wj-print"
                  className="inline-flex items-center gap-2 px-5 py-3 border border-[var(--wr-border)] hover:border-[var(--wr-ink)] rounded-sm">
            <Printer size={16} />
            <span className="font-mono-wr text-[12px] uppercase tracking-[0.2em]">{wj("r.print", locale)}</span>
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================ Main page
export default function WowJourney() {
  const { locale, t } = useLocale();
  const navigate = useNavigate();

  // persisted answers (survive back/next and language switch)
  const [req, setReq] = useState(() => {
    try {
      const saved = window.sessionStorage.getItem("wj_req");
      if (saved) return { ...DEFAULT_REQ, ...JSON.parse(saved) };
    } catch (_) {}
    return DEFAULT_REQ;
  });
  const [step, setStep] = useState(1);
  const [daysChoice, setDaysChoice] = useState("4_7");
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Persist req across refreshes
  useEffect(() => {
    try { window.sessionStorage.setItem("wj_req", JSON.stringify(req)); } catch (_) {}
  }, [req]);

  const update = useCallback((patch) => setReq((r) => ({ ...r, ...patch })), []);

  // --- Feature flag guard
  if (!FEATURE_ENABLED) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24">
        <h1 className="font-editorial text-4xl mb-4">WOW Journey</h1>
        <p className="text-[var(--wr-ink-2)]">
          {locale === "ro" ? "Această funcționalitate nu este activă." : "This feature is not currently enabled."}
        </p>
        <Link to="/" className="text-[var(--wr-red)] underline mt-4 inline-block">
          {locale === "ro" ? "Înapoi acasă" : "Back to home"} →
        </Link>
      </div>
    );
  }

  // ------------------------------------------------- Submit → create plan
  async function doPlan() {
    setLoading(true); setError(null);
    try {
      const body = { ...req, locale };
      // daysChoice resolves to numeric. The visible +/- stepper on each chip range already
      // keeps sightseeing_days within bounds, so here we only clamp to bounds for safety.
      if (daysChoice === "1") body.sightseeing_days = 1;
      else if (daysChoice === "2_3") body.sightseeing_days = Math.max(2, Math.min(3, body.sightseeing_days || 3));
      else if (daysChoice === "4_7") body.sightseeing_days = Math.max(4, Math.min(7, body.sightseeing_days || 5));
      else if (daysChoice === "8_14") body.sightseeing_days = Math.max(8, Math.min(14, body.sightseeing_days || 10));
      body.must_see = (typeof body.must_see === "string" ? body.must_see : (body.must_see || []).join("\n"))
        .split("\n").map((x) => x.trim()).filter(Boolean);
      body.must_avoid = (typeof body.must_avoid === "string" ? body.must_avoid : (body.must_avoid || []).join("\n"))
        .split("\n").map((x) => x.trim()).filter(Boolean);
      const result = await createPlan(body);
      setPlan(result);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Error");
    } finally {
      setLoading(false);
    }
  }

  async function doRefine(action) {
    if (!plan?.plan_id) return;
    setLoading(true); setError(null);
    try {
      const r = await refinePlan(plan.plan_id, action);
      setPlan(r);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }

  function startOver() {
    setPlan(null); setStep(1); setError(null);
    try { window.sessionStorage.removeItem("wj_req"); } catch (_) {}
    setReq(DEFAULT_REQ);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function sendToConcierge() {
    if (!plan?.primary) return;
    const p = plan.primary;
    const header = wj("r.concierge_prefill_header", locale);
    const bases = p.overnight_bases.map((b) => `${locale === "ro" ? b.name_ro : b.name_en} ×${b.nights}`).join(" → ");
    const interests = req.interests.join(", ") || "mixed";
    const msg =
      `${header}\n\n` +
      `• ${wj("r.overnight_bases", locale)}: ${bases}\n` +
      `• ${locale === "ro" ? "Zile" : "Days"}: ${p.total_days}\n` +
      `• ${locale === "ro" ? "Ritm" : "Pace"}: ${req.pace}\n` +
      `• ${locale === "ro" ? "Interese" : "Interests"}: ${interests}\n` +
      `• ${wj("r.budget_label", locale)}: ${p.budget_min_ron.toLocaleString()}–${p.budget_max_ron.toLocaleString()} RON`;
    try { window.sessionStorage.setItem("wj_concierge_prefill", msg); } catch (_) {}
    navigate("/concierge?from=wow-journey");
  }

  // ============================================================ Render Results
  if (plan) {
    const p = plan.primary;
    return (
      <div className="min-h-screen bg-[var(--wr-bg)]">
        <SEO title={locale === "ro" ? "Itinerariul tău | WOW Journey" : "Your itinerary | WOW Journey"} path="/plan" />
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-14 lg:py-20">
          <div className="flex items-center justify-between mb-10 print-hide">
            <button onClick={startOver} data-testid="wj-start-over"
                    className="inline-flex items-center gap-2 text-[13px] text-[var(--wr-ink-2)] hover:text-[var(--wr-red)]">
              <RefreshCw size={14} /> {wj("r.start_over", locale)}
            </button>
            <p className="font-mono-wr text-[11px] uppercase tracking-widest text-[var(--wr-ink-2)]">
              / WOW Journey
            </p>
          </div>

          <CandidateView candidate={p} locale={locale} onConcierge={sendToConcierge} isPrimary />

          {/* Must-see omissions — surfaced BEFORE the alternatives so visitors see them */}
          {(plan.missing_must_see?.length > 0 || plan.unknown_must_see?.length > 0) && (
            <div className="mb-10 border border-[var(--wr-ink-2)]/30 bg-[var(--wr-surface-2)] p-5 rounded-sm" data-testid="wj-must-see-notice">
              {plan.missing_must_see?.length > 0 && (
                <>
                  <p className="font-mono-wr text-[11px] uppercase tracking-[0.3em] text-[var(--wr-red)] mb-3">
                    / {locale === "ro" ? "Locuri obligatorii pe care nu le-am putut include" : "Must-see places we couldn't fit"}
                  </p>
                  <p className="text-[13px] text-[var(--wr-ink-2)] mb-3">
                    {locale === "ro"
                      ? "Acestea sunt prea departe de rută sau nu se potrivesc cu constrângerile. Alege „Mai multă explorare”, extinde zilele sau elimină preferințele conflictuale."
                      : "These are too far from your route or don't match your constraints. Pick \"More exploration\", extend days, or remove conflicting preferences."}
                  </p>
                  <ul className="space-y-1 text-[14px] text-[var(--wr-ink)]">
                    {plan.missing_must_see.map((m, i) => (
                      <li key={i}>
                        • <strong>{m.name}</strong> — {locale === "ro" ? m.reason_ro : m.reason_en}
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {plan.unknown_must_see?.length > 0 && (
                <div className={plan.missing_must_see?.length > 0 ? "mt-5 pt-5 border-t border-[var(--wr-border)]" : ""}>
                  <p className="font-mono-wr text-[11px] uppercase tracking-[0.3em] text-[var(--wr-red)] mb-3">
                    / {locale === "ro" ? "Nume necunoscute" : "Unrecognised names"}
                  </p>
                  <p className="text-[13px] text-[var(--wr-ink-2)] mb-2">
                    {locale === "ro"
                      ? "Nu am putut recunoaște aceste nume în catalogul nostru:"
                      : "We couldn't match these names to our catalog:"}
                  </p>
                  <p className="text-[14px] text-[var(--wr-ink)]">
                    {plan.unknown_must_see.join(", ")}
                  </p>
                </div>
              )}
            </div>
          )}

          {plan.conflicts && plan.conflicts.length > 0 && (
            <div className="mb-10 border border-[var(--wr-red)]/40 bg-[var(--wr-red)]/5 p-5 rounded-sm" data-testid="wj-conflicts">
              <p className="font-mono-wr text-[11px] uppercase tracking-[0.3em] text-[var(--wr-red)] mb-3">
                / {locale === "ro" ? "Câteva lucruri intră în conflict" : "A few things conflict"}
              </p>
              <ul className="space-y-2 text-[14px] text-[var(--wr-ink)]">
                {plan.conflicts.map((c, i) => (
                  <li key={i}>• {locale === "ro" ? c.message_ro : c.message_en}</li>
                ))}
              </ul>
            </div>
          )}

          {plan.alternatives && plan.alternatives.length > 0 && (
            <div className="border-t border-[var(--wr-border)] pt-14">
              {plan.alternatives.map((alt) => (
                <CandidateView key={alt.id} candidate={alt} locale={locale} />
              ))}
            </div>
          )}

          {/* Refinement actions */}
          <div className="mt-10 border border-[var(--wr-border)] bg-[var(--wr-surface-2)] p-6 rounded-sm print-hide" data-testid="wj-refine">
            <p className="font-mono-wr text-[11px] uppercase tracking-[0.3em] text-[var(--wr-red)] mb-4">
              / {wj("r.refine", locale)}
            </p>
            <div className="flex flex-wrap gap-2">
              <Chip onClick={() => doRefine("more_relaxed")} dataTestid="wj-refine-relaxed">{wj("r.refine_relaxed", locale)}</Chip>
              <Chip onClick={() => doRefine("reduce_driving")} dataTestid="wj-refine-driving">{wj("r.refine_reduce_driving", locale)}</Chip>
              <Chip onClick={() => doRefine("keep_one_base")} dataTestid="wj-refine-one-base">{wj("r.refine_one_base", locale)}</Chip>
              <Chip onClick={() => doRefine("remove_hiking")} dataTestid="wj-refine-no-hiking">{wj("r.refine_remove_hiking", locale)}</Chip>
              <Chip onClick={() => doRefine("adjust_budget_up")} dataTestid="wj-refine-budget-up">{wj("r.refine_budget_up", locale)}</Chip>
              <Chip onClick={() => doRefine("adjust_budget_down")} dataTestid="wj-refine-budget-down">{wj("r.refine_budget_down", locale)}</Chip>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="mt-10 text-[12px] text-[var(--wr-ink-2)] italic border-t border-[var(--wr-border)] pt-6" data-testid="wj-disclaimer">
            <p className="mb-1">{locale === "ro" ? plan.disclaimer_ro : plan.disclaimer_en}</p>
          </div>

          {loading && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 print-hide">
              <div className="bg-[var(--wr-bg)] px-8 py-6 rounded-sm">
                <p className="font-mono-wr text-sm">{wj("r.loading", locale)}</p>
              </div>
            </div>
          )}
          {error && <p className="mt-4 text-sm text-red-700" data-testid="wj-error">{error}</p>}
        </div>

        {/* Print CSS */}
        <style>{`
          @media print {
            .print-hide { display: none !important; }
            header, footer, nav { display: none !important; }
            body { background: white !important; color: black !important; }
          }
        `}</style>
      </div>
    );
  }

  // ============================================================ Render Wizard
  return (
    <div className="min-h-screen bg-[var(--wr-bg)]">
      <SEO
        title={locale === "ro" ? "WOW Journey — planifică România" : "WOW Journey — plan Romania"}
        description={locale === "ro" ? WJ.band.desc_ro : WJ.band.desc_en}
        path="/plan"
      />
      <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-14 lg:py-20">
        <div className="mb-10">
          <p className="font-mono-wr text-[11px] uppercase tracking-[0.3em] text-[var(--wr-red)] mb-3">/ WOW Journey</p>
          <h1 className="font-editorial text-4xl sm:text-5xl text-[var(--wr-ink)]">
            {locale === "ro" ? WJ.shell.title_ro : WJ.shell.title_en}
          </h1>
          <p className="mt-3 text-[16px] text-[var(--wr-ink-2)] max-w-xl">
            {locale === "ro" ? WJ.shell.sub_ro : WJ.shell.sub_en}
          </p>
          <p className="mt-6 font-mono-wr text-[11px] uppercase tracking-widest text-[var(--wr-ink-2)]" data-testid="wj-step-indicator">
            {wj("shell.step_label", locale, { n: step })}
          </p>
        </div>

        {/* ================== STEP 1 ================== */}
        {step === 1 && (
          <div data-testid="wj-step-1">
            <h2 className="font-editorial text-2xl mb-6">{wj("s1.heading", locale)}</h2>

            <Section>
              <Label>{wj("s1.where_start", locale)}</Label>
              <div className="flex gap-3 items-center">
                <input type="text" value={req.start_city}
                       disabled={req.start_undecided}
                       onChange={(e) => update({ start_city: e.target.value })}
                       placeholder={wj("s1.search_city", locale)}
                       data-testid="wj-start-city"
                       className="flex-1 bg-[var(--wr-surface-2)] border border-[var(--wr-border)] px-4 py-3 rounded-sm text-[15px] disabled:opacity-40" />
                <label className="flex items-center gap-2 text-[14px]">
                  <input type="checkbox" checked={req.start_undecided}
                         onChange={(e) => update({ start_undecided: e.target.checked })}
                         data-testid="wj-start-undecided" />
                  {wj("s1.undecided", locale)}
                </label>
              </div>
            </Section>

            <Section>
              <Label>{wj("s1.when", locale)}</Label>
              <div className="flex flex-wrap gap-2">
                {["spring","summer","fall","winter"].map((s) => (
                  <Chip key={s} active={req.season === s} onClick={() => update({ season: s })} dataTestid={`wj-season-${s}`}>
                    {wj(`s1.season_${s}`, locale)}
                  </Chip>
                ))}
                <Chip active={req.season === null} onClick={() => update({ season: null })} dataTestid="wj-season-not-sure">
                  {wj("s1.not_sure", locale)}
                </Chip>
              </div>
            </Section>

            <Section>
              <Label>{wj("s1.days", locale)}</Label>
              <div className="flex flex-wrap gap-2">
                {[["1","1"],["2_3","2_3"],["4_7","4_7"],["8_14","8_14"]].map(([k]) => (
                  <Chip key={k} active={daysChoice === k} onClick={() => {
                    setDaysChoice(k);
                    // Set a sensible default within the chip's range so visitors see the resolved number
                    if (k === "1") update({ sightseeing_days: 1 });
                    else if (k === "2_3") update({ sightseeing_days: 3 });
                    else if (k === "4_7") update({ sightseeing_days: 5 });
                    else if (k === "8_14") update({ sightseeing_days: 10 });
                  }} dataTestid={`wj-days-${k}`}>
                    {wj(`s1.d_${k}`, locale)}
                  </Chip>
                ))}
                <Chip active={daysChoice === "custom"} onClick={() => setDaysChoice("custom")} dataTestid="wj-days-custom">
                  {wj("s1.d_custom", locale)}
                </Chip>
              </div>
              {/* Visible resolved day count + inline adjustment — critical so visitors always know the exact number */}
              {daysChoice !== "custom" && daysChoice !== "1" && (
                <div className="mt-4 flex items-center gap-3" data-testid="wj-days-tune">
                  <span className="font-mono-wr text-[11px] uppercase tracking-widest text-[var(--wr-ink-2)]">
                    {locale === "ro" ? "Zile planificate" : "Planned days"}:
                  </span>
                  <button type="button" onClick={() => {
                    const bounds = { "2_3":[2,3], "4_7":[4,7], "8_14":[8,14] }[daysChoice] || [1,30];
                    update({ sightseeing_days: Math.max(bounds[0], (req.sightseeing_days || bounds[0]) - 1) });
                  }} className="w-8 h-8 border border-[var(--wr-border)] rounded-sm hover:border-[var(--wr-ink)]" data-testid="wj-days-minus">−</button>
                  <span className="font-mono-wr text-[16px] text-[var(--wr-ink)] w-8 text-center" data-testid="wj-days-value">{req.sightseeing_days}</span>
                  <button type="button" onClick={() => {
                    const bounds = { "2_3":[2,3], "4_7":[4,7], "8_14":[8,14] }[daysChoice] || [1,30];
                    update({ sightseeing_days: Math.min(bounds[1], (req.sightseeing_days || bounds[0]) + 1) });
                  }} className="w-8 h-8 border border-[var(--wr-border)] rounded-sm hover:border-[var(--wr-ink)]" data-testid="wj-days-plus">+</button>
                </div>
              )}
              {daysChoice === "custom" && (
                <input type="number" min={1} max={30} value={req.sightseeing_days}
                       onChange={(e) => update({ sightseeing_days: Math.max(1, Math.min(30, Number(e.target.value) || 1)) })}
                       data-testid="wj-days-custom-input"
                       className="mt-3 w-40 bg-[var(--wr-surface-2)] border border-[var(--wr-border)] px-4 py-3 rounded-sm" />
              )}
            </Section>

            <Section>
              <label className="flex items-center gap-3 text-[15px]">
                <input type="checkbox" checked={req.visiting_family}
                       onChange={(e) => update({ visiting_family: e.target.checked })}
                       data-testid="wj-visiting-family" />
                {wj("s1.family_toggle", locale)}
              </label>
              {req.visiting_family && (
                <div className="mt-4 grid sm:grid-cols-2 gap-4">
                  <input type="text" value={req.family_base}
                         onChange={(e) => update({ family_base: e.target.value })}
                         placeholder={wj("s1.family_base", locale)}
                         data-testid="wj-family-base"
                         className="bg-[var(--wr-surface-2)] border border-[var(--wr-border)] px-4 py-3 rounded-sm" />
                  <input type="number" min={0} max={30} value={req.family_blocked_days}
                         onChange={(e) => update({ family_blocked_days: Number(e.target.value) || 0 })}
                         placeholder={wj("s1.blocked_days", locale)}
                         data-testid="wj-family-blocked"
                         className="bg-[var(--wr-surface-2)] border border-[var(--wr-border)] px-4 py-3 rounded-sm" />
                </div>
              )}
            </Section>

            <Section>
              <Label>{wj("s1.where_end", locale)}</Label>
              <div className="flex flex-wrap gap-2">
                <Chip active={req.end_city === req.start_city && !req.end_undecided} onClick={() => update({ end_city: req.start_city, end_undecided: false })} dataTestid="wj-end-same">
                  {wj("s1.end_same", locale)}
                </Chip>
                <Chip active={!!req.end_city && req.end_city !== req.start_city} onClick={() => update({ end_city: " ", end_undecided: false })} dataTestid="wj-end-other">
                  {wj("s1.end_other", locale)}
                </Chip>
                <Chip active={req.end_undecided} onClick={() => update({ end_undecided: true, end_city: "" })} dataTestid="wj-end-undecided">
                  {wj("s1.not_sure", locale)}
                </Chip>
              </div>
              {!!req.end_city && req.end_city !== req.start_city && !req.end_undecided && (
                <input type="text" value={req.end_city.trim()} onChange={(e) => update({ end_city: e.target.value })}
                       placeholder={wj("s1.search_city", locale)} data-testid="wj-end-city"
                       className="mt-3 w-full bg-[var(--wr-surface-2)] border border-[var(--wr-border)] px-4 py-3 rounded-sm" />
              )}
            </Section>
          </div>
        )}

        {/* ================== STEP 2 ================== */}
        {step === 2 && (
          <div data-testid="wj-step-2">
            <h2 className="font-editorial text-2xl mb-6">{wj("s2.heading", locale)}</h2>
            <Section>
              <Label>{wj("s2.transport", locale)}</Label>
              <div className="flex flex-wrap gap-2">
                {["car","train","undecided"].map((t_) => (
                  <Chip key={t_} active={req.transport === t_} onClick={() => update({ transport: t_ })} dataTestid={`wj-transport-${t_}`}>
                    {wj(`s2.t_${t_}`, locale)}
                  </Chip>
                ))}
              </div>
            </Section>
            <Section>
              <Label>{wj("s2.interests", locale)}</Label>
              <div className="flex flex-wrap gap-2">
                {["castles_history","towns_culture","scenic_views","food_local","nature_walking","relaxation_wellness","family","surprise_me"].map((i) => {
                  const active = req.interests.includes(i);
                  return (
                    <Chip key={i} active={active} dataTestid={`wj-interest-${i}`}
                          onClick={() => {
                            if (active) update({ interests: req.interests.filter((x) => x !== i) });
                            else if (req.interests.length < 3) update({ interests: [...req.interests, i] });
                          }}>
                      {wj(`s2.i_${i}`, locale)}
                    </Chip>
                  );
                })}
              </div>
            </Section>
            <Section>
              <Label>{wj("s2.pace", locale)}</Label>
              <div className="flex flex-wrap gap-2">
                {["relaxed","balanced","active"].map((p_) => (
                  <Chip key={p_} active={req.pace === p_} onClick={() => update({ pace: p_ })} dataTestid={`wj-pace-${p_}`}>
                    {wj(`s2.p_${p_}`, locale)}
                  </Chip>
                ))}
              </div>
            </Section>
          </div>
        )}

        {/* ================== STEP 3 ================== */}
        {step === 3 && (
          <div data-testid="wj-step-3">
            <h2 className="font-editorial text-2xl mb-6">{wj("s3.heading", locale)}</h2>
            <Section>
              <Label>{wj("s3.group", locale)}</Label>
              <div className="flex flex-wrap gap-2">
                {["solo","couple","family","friends"].map((g_) => (
                  <Chip key={g_} active={req.group === g_} onClick={() => update({ group: g_, group_size: g_ === "solo" ? 1 : Math.max(2, req.group_size) })} dataTestid={`wj-group-${g_}`}>
                    {wj(`s3.g_${g_}`, locale)}
                  </Chip>
                ))}
              </div>
              {req.group !== "solo" && (
                <div className="mt-4 flex items-center gap-4">
                  <Label>{wj("s3.group_size", locale)}</Label>
                  <input type="number" min={1} max={12} value={req.group_size}
                         onChange={(e) => update({ group_size: Math.max(1, Math.min(12, Number(e.target.value) || 1)) })}
                         data-testid="wj-group-size"
                         className="w-24 bg-[var(--wr-surface-2)] border border-[var(--wr-border)] px-3 py-2 rounded-sm" />
                  {req.group === "family" && (
                    <label className="flex items-center gap-2 text-[14px]">
                      <input type="checkbox" checked={req.children} onChange={(e) => update({ children: e.target.checked })} data-testid="wj-children" />
                      {wj("s3.children", locale)}
                    </label>
                  )}
                </div>
              )}
            </Section>
            <Section>
              <Label>{wj("s3.budget", locale)}</Label>
              <div className="flex flex-wrap gap-2">
                {["economical","comfortable","premium","not_sure"].map((b_) => (
                  <Chip key={b_} active={req.budget === b_} onClick={() => update({ budget: b_ })} dataTestid={`wj-budget-${b_}`}>
                    {wj(`s3.b_${b_}`, locale)}
                  </Chip>
                ))}
              </div>
              <p className="mt-2 text-[12px] italic text-[var(--wr-ink-2)]">{wj("s3.budget_note", locale)}</p>
            </Section>
            <Section>
              <Label>{wj("s3.prefs", locale)}</Label>
              <div className="flex flex-wrap gap-2">
                {["no_hiking","minimize_stairs","avoid_long_drives","avoid_mountain_roads","avoid_after_dark","keep_one_base"].map((k) => (
                  <Chip key={k} active={req[k]} onClick={() => update({ [k]: !req[k] })} dataTestid={`wj-pref-${k}`}>
                    {wj(`s3.pref_${k}`, locale)}
                  </Chip>
                ))}
              </div>
            </Section>
            <Section>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>{wj("s3.must_see", locale)}</Label>
                  <textarea rows={3} value={Array.isArray(req.must_see) ? req.must_see.join("\n") : req.must_see}
                            onChange={(e) => update({ must_see: e.target.value })}
                            data-testid="wj-must-see"
                            className="w-full bg-[var(--wr-surface-2)] border border-[var(--wr-border)] px-4 py-3 rounded-sm text-[14px]" />
                </div>
                <div>
                  <Label>{wj("s3.must_avoid", locale)}</Label>
                  <textarea rows={3} value={Array.isArray(req.must_avoid) ? req.must_avoid.join("\n") : req.must_avoid}
                            onChange={(e) => update({ must_avoid: e.target.value })}
                            data-testid="wj-must-avoid"
                            className="w-full bg-[var(--wr-surface-2)] border border-[var(--wr-border)] px-4 py-3 rounded-sm text-[14px]" />
                </div>
              </div>
            </Section>
          </div>
        )}

        {/* Nav buttons */}
        <div className="mt-10 flex items-center justify-between">
          <button
            onClick={() => (step === 1 ? navigate(-1) : setStep(step - 1))}
            data-testid="wj-back"
            className="inline-flex items-center gap-2 px-5 py-3 border border-[var(--wr-border)] hover:border-[var(--wr-ink)] rounded-sm">
            <ArrowLeft size={14} />
            <span className="font-mono-wr text-[12px] uppercase tracking-[0.2em]">{wj("shell.back", locale)}</span>
          </button>

          {step < 3 ? (
            <button onClick={() => setStep(step + 1)} data-testid="wj-next"
                    className="inline-flex items-center gap-2 px-5 py-3 bg-[var(--wr-red)] text-white hover:bg-[var(--wr-red-hover)] rounded-sm">
              <span className="font-mono-wr text-[12px] uppercase tracking-[0.2em]">{wj("shell.next", locale)}</span>
              <ArrowRight size={14} />
            </button>
          ) : (
            <button onClick={doPlan} disabled={loading} data-testid="wj-submit"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-[var(--wr-red)] text-white hover:bg-[var(--wr-red-hover)] rounded-sm disabled:opacity-50">
              <Compass size={14} />
              <span className="font-mono-wr text-[12px] uppercase tracking-[0.2em]">
                {loading ? wj("r.loading", locale) : wj("shell.plan", locale)}
              </span>
            </button>
          )}
        </div>

        {error && <p className="mt-6 text-sm text-red-700" data-testid="wj-error">{error}</p>}
      </div>
    </div>
  );
}
